import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { getPortalType, getUserData, getAuthToken, getActivePortal, getIsLoggingOut } from '@/utils/auth';
import { getAnonymousUserPreferences } from '@/utils/anonymousAuth';
import { trackEvent, attachUserProps } from '@/analytics/analytics';
import { userActivitiesAPI } from '@/api/userActivities';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { useDiscovery } from '@/context/DiscoveryContext';
import { useLiveClass } from '@/context/LiveClassContext';
import { formatBoardLabel } from '@/utils/boardUtils';

const STORAGE_KEY = 'student_subject_chapter_selection';
const CHAPTER_DATA_CACHE_VERSION = 'v4';

const SubjectChapterContext = createContext(null);

export function SubjectChapterProvider({ children, initialSubjects = null }) {
  const initialSelection = (() => {
    try {
      if (typeof window === 'undefined') {
        return {
          selectedSubject: '',
          selectedChapter: '',
          selectedSubjectId: null,
          selectedChapterId: null,
          selectedBoard: '',
          selectedBoardId: null,
          selectedGrade: '',
          selectedGradeId: null,
        };
      }
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return {
          selectedSubject: '',
          selectedChapter: '',
          selectedSubjectId: null,
          selectedChapterId: null,
          selectedBoard: '',
          selectedBoardId: null,
          selectedGrade: '',
          selectedGradeId: null,
        };
      }
      const data = JSON.parse(stored);
      // Ensure IDs are coerced to Strings from start to prevent type mismatch
      return {
        selectedSubject: data?.selectedSubject || '',
        selectedChapter: data?.selectedChapter || '',
        selectedSubjectId: data?.selectedSubjectId ? String(data.selectedSubjectId) : null,
        selectedChapterId: data?.selectedChapterId ? String(data.selectedChapterId) : null,
        selectedBoard: data?.selectedBoard || '',
        selectedBoardId: data?.selectedBoardId ? String(data.selectedBoardId) : null,
        selectedGrade: data?.selectedGrade || '',
        selectedGradeId: data?.selectedGradeId ? String(data.selectedGradeId) : null,
      };
    } catch (error) {
      console.error('Error loading subject/chapter selection from localStorage:', error);
      return {
        selectedSubject: '',
        selectedChapter: '',
        selectedSubjectId: null,
        selectedChapterId: null,
        selectedBoard: '',
        selectedBoardId: null,
        selectedGrade: '',
        selectedGradeId: null,
      };
    }
  })();

  // Main atomic selection state
  const [selection, setSelection] = useState(initialSelection);

  // Map raw API subjects (subject_name) to UI labels (label)
  const mapInitialSubjects = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map(s => ({
      ...s,
      id: String(s.id),
      label: s.label || s.subject_name || s.name || 'Unknown Subject'
    }));
  };

  // Curriculum lists & loading states
  const [subjects, setSubjects] = useState(() => mapInitialSubjects(initialSubjects));
  const [chapters, setChapters] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadedSubjectId, setLoadedSubjectId] = useState(null);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingChapters, setLoadingChapters] = useState(false);

  // Teacher curriculum scoping state
  const [teacherCurriculumTree, setTeacherCurriculumTree] = useState(null);
  const [hasNoAssignedSubjects, setHasNoAssignedSubjects] = useState(false);

  // New chapter-specific data state (Centralized to prevent multi-fetches)
  const [rawSections, setRawSections] = useState([]);
  const [rawAnnotations, setRawAnnotations] = useState([]);
  const [chapterOverview, setChapterOverview] = useState(null);
  const [loadingChapterData, setLoadingChapterData] = useState(false);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  // Force subject/chapter/board/grade IDs to strings when referencing them
  const selectedSubjectId = selection.selectedSubjectId ? String(selection.selectedSubjectId) : null;
  const selectedChapterId = selection.selectedChapterId ? String(selection.selectedChapterId) : null;
  const selectedSubject = selection.selectedSubject;
  const selectedChapter = selection.selectedChapter;
  const selectedBoardId = selection.selectedBoardId ? String(selection.selectedBoardId) : null;
  const selectedGradeId = selection.selectedGradeId ? String(selection.selectedGradeId) : null;
  const selectedBoard = selection.selectedBoard;
  const selectedGrade = selection.selectedGrade;

  let locationPath = '';
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const loc = useLocation();
    locationPath = loc?.pathname || '';
  } catch (e) { }

  let discovery = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    discovery = useDiscovery();
  } catch (e) { }


  const [currentPath, setCurrentPath] = useState(() => {
    return locationPath || (typeof window !== 'undefined' ? (window.location.pathname || window.location.hash || '') : '');
  });

  useEffect(() => {
    if (locationPath && locationPath !== currentPath) {
      setCurrentPath(locationPath);
    }
  }, [locationPath, currentPath]);

  useEffect(() => {
    const handleLocationChange = () => {
      const path = typeof window !== 'undefined' ? (window.location.pathname || window.location.hash || '') : '';
      setCurrentPath(path);
    };
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('login_success', handleLocationChange);
    window.addEventListener('userLoggedIn', handleLocationChange);
    window.addEventListener('profileUpdated', handleLocationChange);
    window.addEventListener('sessionLoggedOut', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('login_success', handleLocationChange);
      window.removeEventListener('userLoggedIn', handleLocationChange);
      window.removeEventListener('profileUpdated', handleLocationChange);
      window.removeEventListener('sessionLoggedOut', handleLocationChange);
    };
  }, []);

  // Clean state on logout
  useEffect(() => {
    const handleLogout = () => {
      setSubjects([]);
      setChapters([]);
      setBoardId(null);
      setGradeId(null);
      setSelection({
        selectedBoardId: null,
        selectedBoard: '',
        selectedGradeId: null,
        selectedGrade: '',
        selectedSubjectId: null,
        selectedSubject: '',
        selectedChapterId: null,
        selectedChapter: ''
      });
    };
    window.addEventListener('sessionLoggedOut', handleLogout);
    return () => {
      window.removeEventListener('sessionLoggedOut', handleLogout);
    };
  }, []);

  const portalType = useMemo(() => {
    return getPortalType(currentPath);
  }, [currentPath]);

  const isTeacher = portalType === 'teacher' || currentPath.startsWith('/teacher') || (getActivePortal() === 'teacher' && !currentPath.startsWith('/student'));

  // Board and Grade detection (Local to context for fetching)
  const [boardId, setBoardId] = useState(selectedBoardId);
  const [gradeId, setGradeId] = useState(selectedGradeId);

  // Sync boardId and gradeId from selection state (crucial for teacher portal and dynamic selections)
  useEffect(() => {
    if (selectedBoardId) {
      setBoardId(prev => (prev !== selectedBoardId ? selectedBoardId : prev));
    }
    if (selectedGradeId) {
      setGradeId(prev => (prev !== selectedGradeId ? selectedGradeId : prev));
    }
  }, [selectedBoardId, selectedGradeId]);

  // Sync boards and grades from discovery if empty (students/guests only)
  useEffect(() => {
    if (isTeacher) return;
    if (boards.length === 0 && discovery?.boards?.length > 0) {
      setBoards(discovery.boards.map(b => ({
        id: String(b.id),
        value: String(b.id),
        label: formatBoardLabel(b) || 'Unknown Board',
        board_code: b.board_code || '',
        board_name: b.board_name || ''
      })));
    }
    if (grades.length === 0 && discovery?.grades?.length > 0) {
      setGrades(discovery.grades.map(g => ({
        id: String(g.id),
        value: String(g.id),
        label: g.grade_name || g.name || g.label || 'Unknown Grade'
      })));
    }
  }, [discovery, boards.length, grades.length, isTeacher]);

  const persistSelection = useCallback((next) => {
    try {
      if (typeof window === 'undefined') return;
      const data = {
        selectedSubject: next.selectedSubject || '',
        selectedChapter: next.selectedChapter || '',
        selectedSubjectId: next.selectedSubjectId ? String(next.selectedSubjectId) : null,
        selectedChapterId: next.selectedChapterId ? String(next.selectedChapterId) : null,
        selectedBoard: next.selectedBoard || '',
        selectedBoardId: next.selectedBoardId ? String(next.selectedBoardId) : null,
        selectedGrade: next.selectedGrade || '',
        selectedGradeId: next.selectedGradeId ? String(next.selectedGradeId) : null,
      };
      if (data.selectedSubjectId || data.selectedBoardId) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving subject/chapter selection to localStorage:', error);
    }
  }, []);

  let liveClassContext = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    liveClassContext = useLiveClass();
  } catch (e) { }

  const isLiveClassLocked = Boolean(liveClassContext?.isCurriculumLocked);
  const studentCallCurriculum = liveClassContext?.studentCurriculum || null;

  const lockedCurriculum = useMemo(() => {
    if (!isTeacher || !isLiveClassLocked || !studentCallCurriculum) {
      return { isLocked: false, boardId: null, gradeId: null, boardName: '', gradeName: '' };
    }
    return {
      isLocked: true,
      boardId: studentCallCurriculum.boardId ? String(studentCallCurriculum.boardId) : null,
      gradeId: studentCallCurriculum.gradeId ? String(studentCallCurriculum.gradeId) : null,
      boardName: studentCallCurriculum.boardName || '',
      gradeName: studentCallCurriculum.gradeName || '',
    };
  }, [isTeacher, isLiveClassLocked, studentCallCurriculum]);

  const preLiveClassSelectionRef = useRef(null);

  // Auto-switch teacher selection to student profile curriculum during active Live Class,
  // and restore teacher's previous selection when Live Class ends.
  useEffect(() => {
    if (!isTeacher) return;

    if (lockedCurriculum.isLocked && lockedCurriculum.boardId && lockedCurriculum.gradeId) {
      if (!preLiveClassSelectionRef.current) {
        preLiveClassSelectionRef.current = { ...selection };
      }

      const targetBoardId = String(lockedCurriculum.boardId);
      const targetGradeId = String(lockedCurriculum.gradeId);

      if (String(selection.selectedBoardId) !== targetBoardId || String(selection.selectedGradeId) !== targetGradeId) {
        if (teacherCurriculumTree?.boards) {
          const activeBoard = teacherCurriculumTree.boards.find(b => String(b.id) === targetBoardId);
          if (activeBoard) {
            const validGrades = activeBoard.grades || [];
            const activeGrade = validGrades.find(g => String(g.id) === targetGradeId) || validGrades[0];
            const validSubjects = activeGrade?.subjects || [];
            const firstSubject = validSubjects[0];
            const validChapters = firstSubject?.chapters || [];
            const firstChapter = validChapters[0];

            const mappedGrades = validGrades.map(g => ({
              id: String(g.id),
              value: String(g.id),
              label: g.grade_name || `Grade ${g.grade_number}`,
              grade_name: g.grade_name,
              grade_number: g.grade_number
            }));
            setGrades(mappedGrades);

            const mappedSubjects = validSubjects.map(s => ({
              id: String(s.id),
              value: String(s.id),
              label: s.subject_name || 'Unknown Subject',
              subject_name: s.subject_name
            }));
            setSubjects(mappedSubjects);

            const mappedChapters = validChapters.map(c => ({
              ...c,
              id: String(c.id),
              value: String(c.id),
              label: c.chapter_name || 'Unknown Chapter',
              chapter_name: c.chapter_name,
              order: c.order,
              file_url: c.file_url,
              file_upload: c.file_upload,
              suggested_chat_topics: c.suggested_chat_topics || []
            }));
            setChapters(mappedChapters);

            const next = {
              selectedBoardId: targetBoardId,
              selectedBoard: lockedCurriculum.boardName || activeBoard.board_code || activeBoard.board_name,
              selectedGradeId: targetGradeId,
              selectedGrade: activeGrade ? activeGrade.grade_name : lockedCurriculum.gradeName,
              selectedSubjectId: firstSubject ? String(firstSubject.id) : null,
              selectedSubject: firstSubject ? firstSubject.subject_name : '',
              selectedChapterId: firstChapter ? String(firstChapter.id) : null,
              selectedChapter: firstChapter ? firstChapter.chapter_name : ''
            };
            setSelection(next);
            persistSelection(next);
            setBoardId(targetBoardId);
            setGradeId(targetGradeId);
          }
        }
      }
    } else if (!lockedCurriculum.isLocked && preLiveClassSelectionRef.current) {
      const saved = preLiveClassSelectionRef.current;
      preLiveClassSelectionRef.current = null;

      if (saved && teacherCurriculumTree?.boards) {
        const activeBoard = teacherCurriculumTree.boards.find(b => String(b.id) === String(saved.selectedBoardId)) || teacherCurriculumTree.boards[0];
        if (activeBoard) {
          const validGrades = activeBoard.grades || [];
          const activeGrade = validGrades.find(g => String(g.id) === String(saved.selectedGradeId)) || validGrades[0];
          const validSubjects = activeGrade?.subjects || [];
          const activeSubject = validSubjects.find(s => String(s.id) === String(saved.selectedSubjectId)) || validSubjects[0];
          const validChapters = activeSubject?.chapters || [];
          const activeChapter = validChapters.find(c => String(c.id) === String(saved.selectedChapterId)) || validChapters[0];

          const mappedGrades = validGrades.map(g => ({
            id: String(g.id),
            value: String(g.id),
            label: g.grade_name || `Grade ${g.grade_number}`,
            grade_name: g.grade_name,
            grade_number: g.grade_number
          }));
          setGrades(mappedGrades);

          const mappedSubjects = validSubjects.map(s => ({
            id: String(s.id),
            value: String(s.id),
            label: s.subject_name || 'Unknown Subject',
            subject_name: s.subject_name
          }));
          setSubjects(mappedSubjects);

          const mappedChapters = validChapters.map(c => ({
            ...c,
            id: String(c.id),
            value: String(c.id),
            label: c.chapter_name || 'Unknown Chapter',
            chapter_name: c.chapter_name,
            order: c.order,
            file_url: c.file_url,
            file_upload: c.file_upload,
            suggested_chat_topics: c.suggested_chat_topics || []
          }));
          setChapters(mappedChapters);

          const next = {
            selectedBoardId: activeBoard ? String(activeBoard.id) : null,
            selectedBoard: activeBoard ? (activeBoard.board_code || activeBoard.board_name) : '',
            selectedGradeId: activeGrade ? String(activeGrade.id) : null,
            selectedGrade: activeGrade ? activeGrade.grade_name : '',
            selectedSubjectId: activeSubject ? String(activeSubject.id) : null,
            selectedSubject: activeSubject ? activeSubject.subject_name : '',
            selectedChapterId: activeChapter ? String(activeChapter.id) : null,
            selectedChapter: activeChapter ? activeChapter.chapter_name : ''
          };
          setSelection(next);
          persistSelection(next);
          if (next.selectedBoardId) setBoardId(next.selectedBoardId);
          if (next.selectedGradeId) setGradeId(next.selectedGradeId);
        }
      }
    }
  }, [isTeacher, lockedCurriculum.isLocked, lockedCurriculum.boardId, lockedCurriculum.gradeId, lockedCurriculum.boardName, lockedCurriculum.gradeName, teacherCurriculumTree, persistSelection, selection.selectedBoardId, selection.selectedGradeId]);

  const fetchTeacherCurriculum = useCallback(async () => {
    if (getIsLoggingOut()) return;
    setLoadingBoards(true);
    setLoadingGrades(true);
    setLoadingSubjects(true);
    try {
      if (getIsLoggingOut()) return;
      const token = getAuthToken('teacher');
      if (!token) return;

      const res = await axios.get(apiUrl('v1/teachers/curriculum/'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || {};
      const boardsList = data.boards || [];
      const totalSubjects = data.total_subjects || 0;

      if (totalSubjects === 0 || boardsList.length === 0) {
        setHasNoAssignedSubjects(true);
        setTeacherCurriculumTree({ boards: [], total_subjects: 0 });
        setBoards([]);
        setGrades([]);
        setSubjects([]);
        setChapters([]);
        setSelection({
          selectedBoardId: null,
          selectedBoard: '',
          selectedGradeId: null,
          selectedGrade: '',
          selectedSubjectId: null,
          selectedSubject: '',
          selectedChapterId: null,
          selectedChapter: ''
        });
        return;
      }

      setHasNoAssignedSubjects(false);
      setTeacherCurriculumTree(data);

      const mappedBoards = boardsList.map(b => ({
        id: String(b.id),
        value: String(b.id),
        label: b.board_code || b.board_name || 'Unknown Board',
        board_code: b.board_code || '',
        board_name: b.board_name || ''
      }));
      setBoards(mappedBoards);

      setSelection(prev => {
        let activeBoard = boardsList.find(b => String(b.id) === String(prev.selectedBoardId));
        if (!activeBoard) {
          activeBoard = boardsList[0];
        }

        const validGrades = activeBoard?.grades || [];
        let activeGrade = validGrades.find(g => String(g.id) === String(prev.selectedGradeId));
        if (!activeGrade) {
          activeGrade = validGrades[0];
        }

        const validSubjects = activeGrade?.subjects || [];
        let activeSubject = validSubjects.find(s => String(s.id) === String(prev.selectedSubjectId));
        if (!activeSubject) {
          activeSubject = validSubjects[0];
        }

        const validChapters = activeSubject?.chapters || [];
        let activeChapter = validChapters.find(c => String(c.id) === String(prev.selectedChapterId));
        if (!activeChapter) {
          activeChapter = validChapters[0];
        }

        const mappedGrades = validGrades.map(g => ({
          id: String(g.id),
          value: String(g.id),
          label: g.grade_name || `Grade ${g.grade_number}`,
          grade_name: g.grade_name,
          grade_number: g.grade_number
        }));
        setGrades(mappedGrades);

        const mappedSubjects = validSubjects.map(s => ({
          id: String(s.id),
          value: String(s.id),
          label: s.subject_name || 'Unknown Subject',
          subject_name: s.subject_name
        }));
        setSubjects(mappedSubjects);

        const mappedChapters = validChapters.map(c => ({
          ...c,
          id: String(c.id),
          value: String(c.id),
          label: c.chapter_name || 'Unknown Chapter',
          chapter_name: c.chapter_name,
          order: c.order,
          file_url: c.file_url,
          file_upload: c.file_upload,
          suggested_chat_topics: c.suggested_chat_topics || []
        }));
        setChapters(mappedChapters);

        const next = {
          selectedBoardId: activeBoard ? String(activeBoard.id) : null,
          selectedBoard: activeBoard ? (activeBoard.board_code || activeBoard.board_name) : '',
          selectedGradeId: activeGrade ? String(activeGrade.id) : null,
          selectedGrade: activeGrade ? activeGrade.grade_name : '',
          selectedSubjectId: activeSubject ? String(activeSubject.id) : null,
          selectedSubject: activeSubject ? activeSubject.subject_name : '',
          selectedChapterId: activeChapter ? String(activeChapter.id) : null,
          selectedChapter: activeChapter ? activeChapter.chapter_name : ''
        };
        persistSelection(next);
        if (next.selectedBoardId) setBoardId(next.selectedBoardId);
        if (next.selectedGradeId) setGradeId(next.selectedGradeId);
        return next;
      });
    } catch (err) {
      console.error('[SubjectChapter] Failed to load teacher curriculum:', err);
    } finally {
      setLoadingBoards(false);
      setLoadingGrades(false);
      setLoadingSubjects(false);
    }
  }, [persistSelection]);

  const fetchBoardsAndGrades = useCallback(async () => {
    if (getIsLoggingOut()) return;
    setLoadingBoards(true);
    setLoadingGrades(true);
    try {
      if (getIsLoggingOut()) return;
      let boardsData = [];
      let gradesData = [];

      // Fallback to public endpoint if empty
      try {
        const bgRes = await axios.get(apiUrl('superadmin/chapters/boards_and_grades/'));
        const bgData = bgRes.data || {};
        if (bgData.boards) {
          boardsData = bgData.boards.map(b => ({
            id: String(b.id),
            value: String(b.id),
            label: formatBoardLabel(b) || 'Unknown Board',
            board_code: b.board_code || '',
            board_name: b.board_name || ''
          }));
        }
        if (bgData.grades) {
          gradesData = bgData.grades.map(g => ({
            id: String(g.id),
            value: String(g.id),
            label: g.grade_name || g.name || g.label || 'Unknown Grade'
          }));
        }
      } catch (bgErr) {
        console.error('[SubjectChapter] Public boards_and_grades endpoint failed:', bgErr);
      }

      // Fallback to discovery context if available
      if (boardsData.length === 0 && discovery?.boards?.length > 0) {
        boardsData = discovery.boards.map(b => ({
          id: String(b.id),
          value: String(b.id),
          label: formatBoardLabel(b) || 'Unknown Board',
          board_code: b.board_code || '',
          board_name: b.board_name || ''
        }));
      }
      if (gradesData.length === 0 && discovery?.grades?.length > 0) {
        gradesData = discovery.grades.map(g => ({
          id: String(g.id),
          value: String(g.id),
          label: g.grade_name || g.name || g.label || 'Unknown Grade'
        }));
      }

      if (boardsData.length > 0) setBoards(boardsData);
      if (gradesData.length > 0) setGrades(gradesData);
    } catch (err) {
      console.error('[SubjectChapter] Failed to load boards/grades:', err);
    } finally {
      setLoadingBoards(false);
      setLoadingGrades(false);
    }
  }, [discovery]);

  useEffect(() => {
    if (isTeacher) {
      fetchTeacherCurriculum();
    }
  }, [isTeacher, fetchTeacherCurriculum]);

  useEffect(() => {
    const handleAuthEvent = (e) => {
      const portal = e?.detail?.portal || e?.detail?.userType;
      if (portal === 'teacher' || isTeacher || window.location.pathname.includes('/teacher')) {
        fetchTeacherCurriculum();
      }
    };
    window.addEventListener('login_success', handleAuthEvent);
    window.addEventListener('userLoggedIn', handleAuthEvent);
    window.addEventListener('profileUpdated', handleAuthEvent);
    return () => {
      window.removeEventListener('login_success', handleAuthEvent);
      window.removeEventListener('userLoggedIn', handleAuthEvent);
      window.removeEventListener('profileUpdated', handleAuthEvent);
    };
  }, [isTeacher, fetchBoardsAndGrades]);

  useEffect(() => {
    if (initialSubjects && Array.isArray(initialSubjects)) {
      const currentHasLabels = subjects.length > 0 && subjects[0].label;
      const isContextChanged = subjects.length > 0 && subjects[0].grade_id && String(subjects[0].grade_id) !== String(gradeId);

      if (subjects.length === 0 || !currentHasLabels || isContextChanged) {
        setSubjects(mapInitialSubjects(initialSubjects));
      }
    }
  }, [initialSubjects, gradeId]);

  // Label Repair & Sync Logic
  // Keeps selection labels in sync with current subjects/chapters lists.
  // Backfills readable names if only IDs were in storage.
  useEffect(() => {
    const sId = selectedSubjectId ? String(selectedSubjectId) : null;
    if (subjects.length > 0 && sId) {
      const found = subjects.find(s => String(s.id) === sId);
      if (found && found.label !== selectedSubject) {
        setSelection(prev => ({ ...prev, selectedSubject: found.label }));
      }
    }
  }, [subjects, selectedSubjectId, selectedSubject]);

  useEffect(() => {
    const cId = selectedChapterId ? String(selectedChapterId) : null;
    if (chapters.length > 0 && cId) {
      const found = chapters.find(c => String(c.id) === cId);
      if (found && found.label !== selectedChapter) {
        setSelection(prev => ({ ...prev, selectedChapter: found.label }));
      }
    }
  }, [chapters, selectedChapterId, selectedSubjectId, selectedChapter]);


  // Sync state between tabs if localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        try {
          if (!e.newValue) {
            setSelection({
              selectedSubject: '',
              selectedChapter: '',
              selectedSubjectId: null,
              selectedChapterId: null,
            });
            return;
          }
          const data = JSON.parse(e.newValue);
          setSelection({
            selectedSubject: data?.selectedSubject || '',
            selectedChapter: data?.selectedChapter || '',
            selectedSubjectId: data?.selectedSubjectId ? String(data.selectedSubjectId) : null,
            selectedChapterId: data?.selectedChapterId ? String(data.selectedChapterId) : null,
          });
        } catch (err) {
          console.error('[SubjectChapter] Error syncing from storage event:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Centralized Board/Grade detection
  useEffect(() => {
    const detectContext = () => {
      if (isTeacher) {
        try {
          const userData = getUserData('teacher');
          if (userData?.board) {
            const bid = String(userData.board.id || userData.board);
            setBoardId(bid);
          }
          if (userData?.grade) {
            const gid = String(userData.grade.id || userData.grade);
            setGradeId(gid);
          }
        } catch (e) {
          console.error('[SubjectChapter] Teacher context detection failed:', e);
        }
        return;
      }
      try {
        // Use full URL or hash for detection in HashRouter
        const path = window.location.hash || window.location.pathname || '';
        const portal = getPortalType(path);
        const userData = getUserData(portal);

        if (!userData) {
          const anonymousPrefs = getAnonymousUserPreferences();
          if (anonymousPrefs?.boardId && anonymousPrefs?.gradeId) {
            setBoardId(String(anonymousPrefs.boardId));
            setGradeId(String(anonymousPrefs.gradeId));
          } else {
            setBoardId(null);
            setGradeId(null);
          }
        } else if (userData.board && userData.grade) {
          const rawBid = userData.board.preview_board_id || userData.board.id || userData.board;
          const rawGid = userData.grade.preview_grade_id || userData.grade.id || userData.grade;
          const bid = (rawBid && String(rawBid) !== 'other') ? String(rawBid) : (userData.is_custom_curriculum ? '1' : String(rawBid));
          const gid = (rawGid && String(rawGid) !== 'custom') ? String(rawGid) : (userData.is_custom_curriculum ? '5' : String(rawGid));

          setBoardId(prev => {
            if (prev && prev !== bid) setSubjects([]);
            return bid;
          });
          setGradeId(prev => {
            if (prev && prev !== gid) {
              setSubjects([]);
              setChapters([]);
            }
            return gid;
          });
        } else {
          // Fallback to individual localStorage keys if nested profile objects are not yet bootstrapped
          let cachedBoardId = localStorage.getItem('student_board_id');
          let cachedGradeId = localStorage.getItem('student_grade_id');
          if (cachedBoardId === 'other') cachedBoardId = '1';
          if (cachedGradeId === 'custom') cachedGradeId = '5';
          if (cachedBoardId && cachedGradeId) {
            setBoardId(prev => {
              if (prev && prev !== cachedBoardId) setSubjects([]);
              return cachedBoardId;
            });
            setGradeId(prev => {
              if (prev && prev !== cachedGradeId) {
                setSubjects([]);
                setChapters([]);
              }
              return cachedGradeId;
            });
          } else {
            setBoardId(null);
            setGradeId(null);
          }
        }
      } catch (e) {
        console.error('[SubjectChapter] Context detection failed:', e);
      }
    };

    detectContext();

    // Listen for events that change auth context
    window.addEventListener('anonymousPreferencesUpdated', detectContext);
    window.addEventListener('login_success', detectContext);
    window.addEventListener('userLoggedIn', detectContext);
    window.addEventListener('storage', detectContext); // For multi-tab session changes
    window.addEventListener('popstate', detectContext); // For navigation changes
    window.addEventListener('hashchange', detectContext); // For hash changes
    window.addEventListener('profileUpdated', detectContext); // Custom event for profile changes

    return () => {
      window.removeEventListener('anonymousPreferencesUpdated', detectContext);
      window.removeEventListener('login_success', detectContext);
      window.removeEventListener('userLoggedIn', detectContext);
      window.removeEventListener('storage', detectContext);
      window.removeEventListener('popstate', detectContext);
      window.removeEventListener('hashchange', detectContext);
      window.removeEventListener('profileUpdated', detectContext);
    };
  }, []);

  // Global Subjects Fetching (Students & Guests only - Teachers use curriculum tree)
  useEffect(() => {
    if (isTeacher) return;
    const activeBoardId = boardId || selectedBoardId;
    const activeGradeId = gradeId || selectedGradeId;

    if (!activeBoardId || !activeGradeId || getIsLoggingOut()) return;

    // Optimization: Skip fetching if already hydrated for THIS grade
    const isAlreadyHydrated = subjects.length > 0 && subjects.every(s => String(s.grade_id || '') === String(activeGradeId));
    if (initialSubjects && isAlreadyHydrated && !isTeacher) {
      console.log('[SubjectChapter] Skipping initial subjects fetch (hydrated from Bootstrap)');
      return;
    }

    const fetchSubjects = async () => {
      if (getIsLoggingOut()) return;
      setLoadingSubjects(true);
      try {
        const anonPrefs = getAnonymousUserPreferences();
        const isCustomGuest = anonPrefs?.isCustomCurriculum;
        const rawList = await subjectsAPI.list({
          board: activeBoardId,
          grade: activeGradeId,
          ...(isCustomGuest ? { is_custom_curriculum: 'true', sample_only: 'true' } : {})
        });
        if (getIsLoggingOut()) return;
        const list = Array.isArray(rawList) ? rawList : (rawList?.results && Array.isArray(rawList.results) ? rawList.results : []);
        const data = list.map(s => ({
          ...s,
          id: String(s.id),
          value: String(s.id),
          label: s.subject_name || s.name || s.label || 'Unknown Subject'
        }));
        setSubjects(data);
        if (data.length > 0) {
          setSelection(prev => {
            const currentSubExists = data.some(s => String(s.id) === String(prev.selectedSubjectId));
            if (!prev.selectedSubjectId || !currentSubExists) {
              const firstSub = data[0];
              return {
                ...prev,
                selectedSubjectId: firstSub.id,
                selectedSubject: firstSub.label,
                selectedChapterId: null,
                selectedChapter: ''
              };
            }
            return prev;
          });
        }
      } catch (err) {
        if (!getIsLoggingOut()) {
          console.error('[SubjectChapter] Failed to load global subjects:', err);
        }
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [boardId, gradeId, selectedBoardId, selectedGradeId, isTeacher]);

  // Global Chapters Fetching (Students & Guests only - Teachers use curriculum tree)
  useEffect(() => {
    if (isTeacher) return;
    const activeBoardId = boardId || selectedBoardId;
    const activeGradeId = gradeId || selectedGradeId;

    if (!selectedSubjectId || !activeBoardId || !activeGradeId || getIsLoggingOut()) {
      setChapters([]);
      setLoadedSubjectId(null);
      return;
    }

    setLoadedSubjectId(null); // Mark current chapters list as loading/stale for validation
    const fetchChapters = async () => {
      if (getIsLoggingOut()) return;
      setLoadingChapters(true);
      try {
        const anonPrefs = getAnonymousUserPreferences();
        const isCustomGuest = anonPrefs?.isCustomCurriculum;
        const rawList = await chaptersAPI.list({
          subject: selectedSubjectId,
          board: activeBoardId,
          grade: activeGradeId,
          ...(isCustomGuest ? { is_custom_curriculum: 'true', sample_only: 'true' } : {})
        });
        if (getIsLoggingOut()) return;
        const list = Array.isArray(rawList) ? rawList : (rawList?.results && Array.isArray(rawList.results) ? rawList.results : []);
        const data = list
          .filter(c => c && c.is_active !== false) // Default to true if missing, but we expect it to be there
          .map(c => ({
            ...c,
            id: String(c.id),
            value: String(c.id),
            label: c.chapter_name || c.name || c.label || 'Unknown Chapter',
            file_url: c.file_url,
            file_upload: c.file_upload,
            suggested_chat_topics: c.suggested_chat_topics || []
          }));
        setChapters(data);
        setLoadedSubjectId(selectedSubjectId);
        if (data.length > 0) {
          setSelection(prev => {
            const currentChapExists = data.some(c => String(c.id) === String(prev.selectedChapterId));
            if (!prev.selectedChapterId || !currentChapExists) {
              const firstChap = data[0];
              return {
                ...prev,
                selectedChapterId: firstChap.id,
                selectedChapter: firstChap.label
              };
            }
            return prev;
          });
        }

        // FIX E: Idle-time prefetch for adjacent chapters.
        // After the chapters list loads, silently pre-warm localStorage for the first
        // chapters the student is likely to visit. This makes the FIRST click on any
        // chapter instant, not just repeated visits. Runs only during browser idle time
        // so it never competes with the active render. Silent-fail by design.
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window && data.length > 0) {
          // Respect low-bandwidth / data-saver mode — skip prefetch if active.
          const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
          const isSaveData = connection?.saveData === true;
          if (!isSaveData) {
            window.requestIdleCallback(
              () => {
                // Pre-warm the first 3 chapters that are not yet cached.
                // Slice from the beginning of the list (most-likely first visit order).
                const CACHE_VERSION = 'v4';
                const CHAPTERS_TO_WARM = 3;
                const candidates = data.slice(0, CHAPTERS_TO_WARM);

                candidates.forEach(ch => {
                  const chId = String(ch.id);
                  const sectionsKey = `sections_${CACHE_VERSION}_${chId}`;
                  const annKey = `annotations_${CACHE_VERSION}_${chId}`;

                  const alreadyCached = (() => {
                    try {
                      const s = localStorage.getItem(sectionsKey);
                      const a = localStorage.getItem(annKey);
                      if (!s || !a) return false;
                      const CACHE_TTL = 60 * 60 * 1000; // 1 hour (matches main fetch logic)
                      const sTs = Number(JSON.parse(s)?.timestamp || 0);
                      const aTs = Number(JSON.parse(a)?.timestamp || 0);
                      const latest = Math.max(sTs, aTs);
                      return latest && (Date.now() - latest < CACHE_TTL);
                    } catch {
                      return false;
                    }
                  })();

                  if (!alreadyCached) {
                    Promise.all([
                      chaptersAPI.getSections(chId),
                      chaptersAPI.getAnnotations({ chapter: chId })
                    ]).then(([sectionsData, annotationsData]) => {
                      try {
                        const normalizedSections = Array.isArray(sectionsData?.sections)
                          ? sectionsData.sections
                          : (Array.isArray(sectionsData) ? sectionsData : []);
                        const normalizedAnnotations = Array.isArray(annotationsData?.results)
                          ? annotationsData.results
                          : (Array.isArray(annotationsData) ? annotationsData : []);
                        const now = Date.now();
                        localStorage.setItem(sectionsKey, JSON.stringify({ data: normalizedSections, timestamp: now }));
                        localStorage.setItem(annKey, JSON.stringify({ data: normalizedAnnotations, timestamp: now }));
                      } catch {
                        // Ignore localStorage quota errors — best-effort only
                      }
                    }).catch(() => {
                      // Silent fail — prefetch is best-effort, never block or alert user
                    });
                  }
                });
              },
              { timeout: 4000 } // Give the main render 4 s head-start before prefetching
            );
          }
        }
      } catch (err) {
        console.error('[SubjectChapter] Failed to load global chapters:', err);
      } finally {
        setLoadingChapters(false);
      }
    };

    fetchChapters();
  }, [selectedSubjectId, boardId, gradeId]);

  // Centralized Chapter Data Fetching (Sections and Annotations)
  useEffect(() => {
    if (!selectedChapterId) {
      setRawSections([]);
      setRawAnnotations([]);
      setLoadingChapterData(false);
      return;
    }

    let isCancelled = false;

    const fetchChapterData = async () => {
      // Sections/annotations can change from superadmin, so keep cache short
      // and always revalidate in the background to pick up icon/media changes.
      // 1 Hour TTL for section content (Annotations/Nodes)
      const CACHE_TTL = 1 * 60 * 60 * 1000;
      const sectionsKey = `sections_${CHAPTER_DATA_CACHE_VERSION}_${selectedChapterId}`;
      const annKey = `annotations_${CHAPTER_DATA_CACHE_VERSION}_${selectedChapterId}`;

      const cachedSections = localStorage.getItem(sectionsKey);
      const cachedAnn = localStorage.getItem(annKey);
      let hydratedFromCache = false;

      if (cachedSections && cachedAnn) {
        try {
          const s = JSON.parse(cachedSections);
          const a = JSON.parse(cachedAnn);
          const sectionsTimestamp = Number(s?.timestamp || 0);
          const annTimestamp = Number(a?.timestamp || 0);
          const latestTimestamp = Math.max(sectionsTimestamp, annTimestamp);
          if (latestTimestamp && (Date.now() - latestTimestamp < CACHE_TTL)) {
            const normalizedSections = Array.isArray(s.data?.sections)
              ? s.data.sections
              : (Array.isArray(s.data) ? s.data : []);
            const normalizedAnnotations = Array.isArray(a.data?.results)
              ? a.data.results
              : (Array.isArray(a.data) ? a.data : []);

            if (isCancelled) return;
            setRawSections(normalizedSections);
            setRawAnnotations(normalizedAnnotations);
            setLoadingChapterData(false);
            hydratedFromCache = true;
          }
        } catch (_) { }
      }

      if (!hydratedFromCache) {
        setRawSections([]);
        setRawAnnotations([]);
        setLoadingChapterData(true);
      }

      try {
        const path = window.location.hash || window.location.pathname || '';
        const portal = getPortalType(path);
        const token = getAuthToken(portal);
        const completionsPromise = token
          ? userActivitiesAPI.getChapterCompletions(selectedChapterId).catch(err => {
            console.error('[SubjectChapter] Failed to load activity completions:', err);
            return [];
          })
          : Promise.resolve([]);

        let overviewData = null;
        let sectionsData = null;
        let annotationsData = null;
        let completionsData = null;

        try {
          const overview = await chaptersAPI.getChapterOverview(selectedChapterId);
          if (overview) {
            overviewData = overview;
            if (Array.isArray(overview.sections)) {
              sectionsData = { sections: overview.sections };
            }
            if (Array.isArray(overview.annotations)) {
              annotationsData = { results: overview.annotations };
            }
            if (Array.isArray(overview.completions)) {
              completionsData = overview.completions;
            }
          }
        } catch (overviewErr) {
          console.warn('[SubjectChapter] Composite overview failed, falling back to granular endpoints:', overviewErr?.message);
        }

        // Granular fallbacks only executed if composite overview failed or omitted data
        const fallbackPromises = [];
        if (!sectionsData) {
          fallbackPromises.push(
            chaptersAPI.getSections(selectedChapterId)
              .then(data => { sectionsData = data; })
              .catch(err => {
                console.warn('[SubjectChapter] Failed to fetch sections for chapter:', selectedChapterId, err?.message);
                sectionsData = { sections: [] };
              })
          );
        }
        if (!annotationsData) {
          fallbackPromises.push(
            chaptersAPI.getAnnotations({ chapter: selectedChapterId })
              .then(data => { annotationsData = data; })
              .catch(err => {
                console.warn('[SubjectChapter] Failed to fetch annotations for chapter:', selectedChapterId, err?.message);
                annotationsData = { results: [] };
              })
          );
        }
        if (!completionsData && token) {
          fallbackPromises.push(
            completionsPromise.then(data => { completionsData = data; })
          );
        }

        if (fallbackPromises.length > 0) {
          await Promise.all(fallbackPromises);
        }

        const normalizedSections = Array.isArray(sectionsData?.sections)
          ? sectionsData.sections
          : (Array.isArray(sectionsData) ? sectionsData : []);
        const normalizedAnnotations = Array.isArray(annotationsData?.results)
          ? annotationsData.results
          : (Array.isArray(annotationsData) ? annotationsData : []);

        if (isCancelled) return;
        setRawSections(normalizedSections);
        setRawAnnotations(normalizedAnnotations);
        if (overviewData) {
          setChapterOverview(overviewData);
          if (overviewData.bookmarks) {
            try { localStorage.setItem(`chapter_${selectedChapterId}_bookmarks`, JSON.stringify(overviewData.bookmarks)); } catch (_) {}
          }
          if (overviewData.highlights) {
            try { localStorage.setItem(`chapter_${selectedChapterId}_highlights`, JSON.stringify(overviewData.highlights)); } catch (_) {}
          }
          if (overviewData.annotation_positions) {
            try { localStorage.setItem(`chapter_${selectedChapterId}_positions`, JSON.stringify(overviewData.annotation_positions)); } catch (_) {}
          }
        }

        const now = Date.now();
        localStorage.setItem(sectionsKey, JSON.stringify({ data: normalizedSections, timestamp: now }));
        localStorage.setItem(annKey, JSON.stringify({ data: normalizedAnnotations, timestamp: now }));

        // Sync completions to localStorage & dispatch change event
        if (token && Array.isArray(completionsData)) {
          const completionsMap = {};
          completionsData.forEach(completion => {
            const annotationId = completion.annotation_id || completion.annotation?.id;
            if (annotationId) {
              completionsMap[String(annotationId)] = {
                completedAt: completion.completed_at || new Date().toISOString(),
                xpEarned: completion.xp_earned || 0,
                gemsEarned: completion.gems_earned || 0
              };
            }
          });

          const packId = `chapter_${selectedChapterId}`;
          const progressKey = `learningPack_${packId}_progress`;
          const existingLocalData = JSON.parse(localStorage.getItem(progressKey) || '{}');

          // Merge backend completions. If the backend has record of it, it will overwrite.
          const mergedCompletions = {
            ...existingLocalData,
            ...completionsMap
          };

          if (Object.keys(mergedCompletions).length > 0) {
            localStorage.setItem(progressKey, JSON.stringify(mergedCompletions));
          } else {
            localStorage.removeItem(progressKey);
          }

          window.dispatchEvent(
            new CustomEvent('learningPackProgressChanged', {
              detail: {
                packId,
                storageKey: progressKey,
              },
            })
          );
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('[SubjectChapter] Failed to load chapter data:', err);
        if (!hydratedFromCache) {
          setRawSections([]);
          setRawAnnotations([]);
        }
      } finally {
        if (!isCancelled && !hydratedFromCache) {
          setLoadingChapterData(false);
        }
      }
    };

    fetchChapterData();

    return () => {
      isCancelled = true;
    };
  }, [selectedChapterId]);

  // Normalization logic for activities/annotations
  const annotations = useMemo(() => {
    if (!Array.isArray(rawAnnotations)) return [];

    const normalizeType = (rawType) => {
      const raw = String(rawType || '').trim();
      const lower = raw.toLowerCase();
      if (lower === 'basicconcepts' || lower === 'basic concepts') return 'Watch';
      if (!raw) return 'Watch';
      const known = ['Watch', 'Video', 'Explore', 'Practice', 'Curiosity', 'Cards'];
      const match = known.find(k => k.toLowerCase() === lower);
      return match || raw;
    };

    const getBaseRewards = (type) => {
      const rewards = {
        Watch: { xp: 5, gems: 0 },
        Video: { xp: 5, gems: 0 },
        Explore: { xp: 3, gems: 0 },
        Practice: { xp: 5, gems: 0 },
        Curiosity: { xp: 3, gems: 0 },
        Cards: { xp: 2, gems: 0 },
      };
      return rewards[type] || { xp: 5, gems: 0 };
    };

    const getDefaultTitle = (type) => {
      switch (type) {
        case 'Watch': return 'Learn and Discover';
        case 'Video': return 'Watch Video';
        case 'Explore': return 'Explore Interactive';
        case 'Practice': return 'Practice Questions';
        case 'Curiosity': return 'Curiosity Corner';
        case 'Cards': return 'Knowledge Cards';
        default: return `${type} Activity`;
      }
    };

    return rawAnnotations.map((ann) => {
      const type = normalizeType(ann.annotation_type);
      const baseRewards = getBaseRewards(type);
      const rawTitle = typeof ann.title === 'string' ? ann.title.trim() : '';

      const resolvedTitle = (() => {
        if (type === 'Watch' && rawTitle === 'Watch Video') return getDefaultTitle('Watch');
        if (type === 'Video' && rawTitle === 'Watch Video') return '';
        if (!rawTitle) return type === 'Video' ? '' : getDefaultTitle(type);
        return rawTitle;
      })();

      const sectionName = typeof (ann.section_name ?? ann.sectionName) === 'string' ? (ann.section_name ?? ann.sectionName).trim() : null;
      const usableSectionName = sectionName && !sectionName.includes('|') ? sectionName : null;
      const sectionNumber = typeof (ann.section_number ?? ann.sectionNumber) === 'string' ? (ann.section_number ?? ann.sectionNumber).trim() : null;
      const oneLiner = typeof (ann.one_liner ?? ann.oneLiner) === 'string' ? (ann.one_liner ?? ann.oneLiner).trim() : null;

      return {
        id: String(ann.id),
        annotation_id: ann.id,
        annotation_type: type,
        type,
        icon_type: ann.icon_type || ann.iconType || null,
        page_number: ann.page_number ?? ann.pageNumber ?? null,
        position_top: ann.position_top ?? ann.positionTop ?? null,
        position_left: ann.position_left ?? ann.positionLeft ?? null,
        duration: ann.duration_seconds
          ? `${Math.max(Math.ceil(ann.duration_seconds / 60), 1)} min`
          : (ann.duration || (type === 'Watch' ? '10 min' : '5 min')),
        durationSeconds: ann.duration_seconds || 0,
        title: resolvedTitle,
        description: resolvedTitle ? `${type} activity: ${resolvedTitle}` : undefined,
        one_liner: oneLiner || undefined,
        video_file_url: ann.video_file_url || undefined,
        youtube_url: ann.youtube_url || undefined,
        chapter_id: ann.chapter?.id || ann.chapter || selectedChapterId,
        section_name: usableSectionName,
        section_number: sectionNumber,
        xpReward: baseRewards.xp,
        gemsReward: baseRewards.gems,
      };
    }).filter((it) => ['Watch', 'Video', 'Explore', 'Practice', 'Curiosity', 'Cards'].includes(it.type));
  }, [rawAnnotations, selectedChapterId]);

  const sections = useMemo(() => {
    const normalizedSections = Array.isArray(rawSections?.sections)
      ? rawSections.sections
      : (Array.isArray(rawSections) ? rawSections : []);

    return normalizedSections.map(s => ({
      ...s,
      id: (() => {
        const rawId = s.id ?? s.section_id ?? s.sectionId ?? null;
        if (rawId === null || rawId === undefined || rawId === '') return null;
        return String(rawId);
      })(),
      section_id: s.section_id || s.sectionId || s.id || null,
      section_number: s.section_number || s.sectionNumber || null,
      section_name: s.section_name || s.title || s.sectionName || null,
      section_full: s.section_full || s.fullTitle || null,
      page_number: s.page_number || s.pageNumber || null,
      label: s.section_full || s.fullTitle || s.section_name || s.title || s.label || `Section ${s.section_number || s.sectionNumber}`
    }));
  }, [rawSections]);


  const isDebugEnabled = useCallback(() => {
    try {
      if (typeof window === 'undefined') return false;
      const v = window.localStorage.getItem('debug_subject_chapter_selection');
      return v === '1' || v === 'true' || v === 'yes';
    } catch { return false; }
  }, []);

  const debugLog = useCallback((event, payload) => {
    if (!isDebugEnabled()) return;
    try {
      const entry = {
        ts: new Date().toISOString(),
        event,
        payload,
        stack: new Error('selection update trace').stack,
      };
      if (typeof window !== 'undefined') {
        window.__subjectChapterSelectionDebug = window.__subjectChapterSelectionDebug || [];
        window.__subjectChapterSelectionDebug.push(entry);
        if (window.__subjectChapterSelectionDebug.length > 25) {
          window.__subjectChapterSelectionDebug.splice(0, window.__subjectChapterSelectionDebug.length - 25);
        }
      }
      console.log(`[SubjectChapter] ${event}`, payload);
    } catch { }
  }, [isDebugEnabled]);

  const clearSelection = useCallback(() => {
    const next = {
      selectedSubject: '',
      selectedChapter: '',
      selectedSubjectId: null,
      selectedChapterId: null,
      selectedBoard: '',
      selectedBoardId: null,
      selectedGrade: '',
      selectedGradeId: null,
    };
    debugLog('clearSelection', { prev: selection, next });
    setSelection(next);
    try { localStorage.removeItem(STORAGE_KEY); } catch { }
  }, [debugLog, selection]);

  const updateSubject = useCallback((subjectId, subjectLabel) => {
    if (isTeacher && teacherCurriculumTree?.boards) {
      const activeBoard = teacherCurriculumTree.boards.find(b => String(b.id) === String(selectedBoardId));
      const activeGrade = activeBoard?.grades?.find(g => String(g.id) === String(selectedGradeId));
      const activeSubject = activeGrade?.subjects?.find(s => String(s.id) === String(subjectId));
      const validChapters = activeSubject?.chapters || [];
      const firstChapter = validChapters[0];

      const mappedChapters = validChapters.map(c => ({
        ...c,
        id: String(c.id),
        value: String(c.id),
        label: c.chapter_name || 'Unknown Chapter',
        chapter_name: c.chapter_name,
        order: c.order,
        file_url: c.file_url,
        file_upload: c.file_upload,
        suggested_chat_topics: c.suggested_chat_topics || []
      }));
      setChapters(mappedChapters);

      setSelection((prev) => {
        const next = {
          ...prev,
          selectedSubject: subjectLabel || (activeSubject ? activeSubject.subject_name : ''),
          selectedSubjectId: subjectId ? String(subjectId) : null,
          selectedChapter: firstChapter ? firstChapter.chapter_name : '',
          selectedChapterId: firstChapter ? String(firstChapter.id) : null,
        };
        debugLog('updateSubject', { args: { subjectId, subjectLabel }, prev, next });
        persistSelection(next);
        try {
          trackEvent('subject_selected', attachUserProps({
            page: 'curriculum_selector',
            subject_id: subjectId ? Number(subjectId) : null,
            subject_name: subjectLabel || '',
          }));
        } catch { }
        return next;
      });
      return;
    }

    setSelection((prev) => {
      const next = {
        ...prev,
        selectedSubject: subjectLabel || '',
        selectedSubjectId: subjectId ? String(subjectId) : null,
        selectedChapter: '',
        selectedChapterId: null,
      };
      debugLog('updateSubject', { args: { subjectId, subjectLabel }, prev, next });
      persistSelection(next);
      try {
        trackEvent('subject_selected', attachUserProps({
          page: 'curriculum_selector',
          subject_id: subjectId ? Number(subjectId) : null,
          subject_name: subjectLabel || '',
        }));
      } catch { }
      return next;
    });
  }, [persistSelection, debugLog, isTeacher, teacherCurriculumTree, selectedBoardId, selectedGradeId]);

  const updateChapter = useCallback((chapterId, chapterLabel) => {
    setSelection((prev) => {
      const next = {
        ...prev,
        selectedChapter: chapterLabel || '',
        selectedChapterId: chapterId ? String(chapterId) : null,
      };
      debugLog('updateChapter', { args: { chapterId, chapterLabel }, prev, next });
      persistSelection(next);
      try {
        trackEvent('chapter_selected', attachUserProps({
          page: 'curriculum_selector',
          subject_id: prev.selectedSubjectId ? Number(prev.selectedSubjectId) : null,
          subject_name: prev.selectedSubject || '',
          chapter_id: chapterId ? Number(chapterId) : null,
          chapter_name: chapterLabel || '',
        }));
      } catch { }
      return next;
    });
  }, [persistSelection, debugLog]);

  const updateSelection = useCallback((subjectId, subjectLabel, chapterId, chapterLabel) => {
    setSelection((prev) => {
      const next = {
        ...prev,
        selectedSubject: subjectLabel || '',
        selectedSubjectId: subjectId ? String(subjectId) : null,
        selectedChapter: chapterLabel || '',
        selectedChapterId: chapterId ? String(chapterId) : null,
      };
      debugLog('updateSelection', { args: { subjectId, subjectLabel, chapterId, chapterLabel }, prev, next });
      persistSelection(next);
      return next;
    });
  }, [persistSelection, debugLog]);

  const updateBoard = useCallback((bId, bLabel) => {
    if (lockedCurriculum.isLocked && lockedCurriculum.boardId && String(bId) !== String(lockedCurriculum.boardId)) {
      console.warn(`[SubjectChapter] Board selection locked to student's syllabus (${lockedCurriculum.boardName}) during active Live Class.`);
      return;
    }

    if (isTeacher && teacherCurriculumTree?.boards) {
      const activeBoard = teacherCurriculumTree.boards.find(b => String(b.id) === String(bId));
      const validGrades = activeBoard?.grades || [];
      const firstGrade = validGrades[0];
      const validSubjects = firstGrade?.subjects || [];
      const firstSubject = validSubjects[0];
      const validChapters = firstSubject?.chapters || [];
      const firstChapter = validChapters[0];

      const mappedGrades = validGrades.map(g => ({
        id: String(g.id),
        value: String(g.id),
        label: g.grade_name || `Grade ${g.grade_number}`,
        grade_name: g.grade_name,
        grade_number: g.grade_number
      }));
      setGrades(mappedGrades);

      const mappedSubjects = validSubjects.map(s => ({
        id: String(s.id),
        value: String(s.id),
        label: s.subject_name || 'Unknown Subject',
        subject_name: s.subject_name
      }));
      setSubjects(mappedSubjects);

      const mappedChapters = validChapters.map(c => ({
        ...c,
        id: String(c.id),
        value: String(c.id),
        label: c.chapter_name || 'Unknown Chapter',
        chapter_name: c.chapter_name,
        order: c.order,
        file_url: c.file_url,
        file_upload: c.file_upload,
        suggested_chat_topics: c.suggested_chat_topics || []
      }));
      setChapters(mappedChapters);

      const next = {
        selectedBoardId: bId ? String(bId) : null,
        selectedBoard: bLabel || (activeBoard ? (activeBoard.board_code || activeBoard.board_name) : ''),
        selectedGradeId: firstGrade ? String(firstGrade.id) : null,
        selectedGrade: firstGrade ? firstGrade.grade_name : '',
        selectedSubjectId: firstSubject ? String(firstSubject.id) : null,
        selectedSubject: firstSubject ? firstSubject.subject_name : '',
        selectedChapterId: firstChapter ? String(firstChapter.id) : null,
        selectedChapter: firstChapter ? firstChapter.chapter_name : ''
      };
      setSelection(next);
      persistSelection(next);
      setBoardId(bId ? String(bId) : null);
      setGradeId(firstGrade ? String(firstGrade.id) : null);
      return;
    }

    const userData = getUserData('teacher');
    const profileGradeId = userData?.grade?.id ? String(userData.grade.id) : (userData?.grade ? String(userData.grade) : null);

    setSelection(prev => {
      const targetGradeId = prev.selectedGradeId || profileGradeId || (grades.length > 0 ? grades[0].id : null);
      const targetGradeObj = grades.find(g => String(g.id) === String(targetGradeId));
      const next = {
        ...prev,
        selectedBoardId: bId ? String(bId) : null,
        selectedBoard: bLabel || '',
        selectedGradeId: targetGradeId,
        selectedGrade: targetGradeObj ? targetGradeObj.label : (prev.selectedGrade || ''),
        selectedSubjectId: null,
        selectedSubject: '',
        selectedChapterId: null,
        selectedChapter: ''
      };
      persistSelection(next);
      return next;
    });
    setBoardId(bId ? String(bId) : null);
    setSubjects([]);
    setChapters([]);
  }, [persistSelection, grades, isTeacher, teacherCurriculumTree, lockedCurriculum]);

  const updateGrade = useCallback((gId, gLabel) => {
    if (lockedCurriculum.isLocked && lockedCurriculum.gradeId && String(gId) !== String(lockedCurriculum.gradeId)) {
      console.warn(`[SubjectChapter] Grade selection locked to student's grade (${lockedCurriculum.gradeName}) during active Live Class.`);
      return;
    }

    if (isTeacher && teacherCurriculumTree?.boards) {
      const activeBoard = teacherCurriculumTree.boards.find(b => String(b.id) === String(selectedBoardId));
      const activeGrade = activeBoard?.grades?.find(g => String(g.id) === String(gId));
      const validSubjects = activeGrade?.subjects || [];
      const firstSubject = validSubjects[0];
      const validChapters = firstSubject?.chapters || [];
      const firstChapter = validChapters[0];

      const mappedSubjects = validSubjects.map(s => ({
        id: String(s.id),
        value: String(s.id),
        label: s.subject_name || 'Unknown Subject',
        subject_name: s.subject_name
      }));
      setSubjects(mappedSubjects);

      const mappedChapters = validChapters.map(c => ({
        ...c,
        id: String(c.id),
        value: String(c.id),
        label: c.chapter_name || 'Unknown Chapter',
        chapter_name: c.chapter_name,
        order: c.order,
        file_url: c.file_url,
        file_upload: c.file_upload,
        suggested_chat_topics: c.suggested_chat_topics || []
      }));
      setChapters(mappedChapters);

      setSelection(prev => {
        const next = {
          ...prev,
          selectedGradeId: gId ? String(gId) : null,
          selectedGrade: gLabel || (activeGrade ? activeGrade.grade_name : ''),
          selectedSubjectId: firstSubject ? String(firstSubject.id) : null,
          selectedSubject: firstSubject ? firstSubject.subject_name : '',
          selectedChapterId: firstChapter ? String(firstChapter.id) : null,
          selectedChapter: firstChapter ? firstChapter.chapter_name : ''
        };
        persistSelection(next);
        return next;
      });
      setGradeId(gId ? String(gId) : null);
      return;
    }

    setSelection(prev => {
      const next = {
        ...prev,
        selectedGradeId: gId ? String(gId) : null,
        selectedGrade: gLabel || '',
        selectedSubjectId: null,
        selectedSubject: '',
        selectedChapterId: null,
        selectedChapter: ''
      };
      persistSelection(next);
      return next;
    });
    setGradeId(gId ? String(gId) : null);
    setSubjects([]);
    setChapters([]);
  }, [persistSelection, isTeacher, teacherCurriculumTree, selectedBoardId, lockedCurriculum]);

  // AUTO-SELECTION: If subjects loaded but none selected, pick the first one (students/guests only).
  useEffect(() => {
    if (isTeacher) return;
    if (!selectedSubjectId && subjects.length > 0 && !loadingSubjects) {
      const firstSubject = subjects[0];
      updateSubject(firstSubject.id, firstSubject.label);
    }
  }, [isTeacher, selectedSubjectId, subjects, loadingSubjects, updateSubject]);

  // AUTO-SELECTION: If chapters loaded but none selected for current subject, pick the first chapter (students/guests only).
  useEffect(() => {
    if (isTeacher) return;
    if (selectedSubjectId && !selectedChapterId && chapters.length > 0 && !loadingChapters) {
      const firstChapter = chapters[0];
      updateChapter(firstChapter.id, firstChapter.label);
    }
  }, [isTeacher, selectedSubjectId, selectedChapterId, chapters, loadingChapters, updateChapter]);

  // Validate selected subject/chapter when lists change.
  useEffect(() => {
    if (!loadingSubjects && subjects.length > 0 && selectedSubjectId) {
      const subjectExists = subjects.some((subject) => String(subject.id) === String(selectedSubjectId));
      if (!subjectExists) {
        console.warn(`[SubjectChapter] Selected subject ${selectedSubjectId} not found. Clearing.`);
        updateSubject(null, '');
        return;
      }
    }

    if (!loadingChapters && chapters.length > 0 && selectedChapterId && selectedSubjectId === loadedSubjectId) {
      const chapterExists = chapters.some((chapter) => String(chapter.id) === String(selectedChapterId));
      if (!chapterExists) {
        console.warn(`[SubjectChapter] Selected chapter ${selectedChapterId} not found. Clearing.`);
        updateChapter(null, '');
      }
    }
  }, [subjects, loadingSubjects, chapters, loadingChapters, selectedSubjectId, selectedChapterId, loadedSubjectId, updateSubject, updateChapter]);

  const value = {
    // Current Selection
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    selectedBoard,
    selectedBoardId,
    selectedGrade,
    selectedGradeId,

    // Global Metadata Lists
    subjects,
    chapters,
    boards,
    grades,
    loadingSubjects,
    loadingChapters,
    loadingBoards,
    loadingGrades,
    hasNoAssignedSubjects,

    // Context metadata (mostly for debugging/internal use)
    boardId,
    gradeId,

    // Handlers
    updateSubject,
    updateChapter,
    updateSelection,
    clearSelection,
    updateBoard,
    updateGrade,

    // Chapter Data
    sections,
    annotations,
    chapterOverview,
    loadingChapterData,
    isPdfViewerOpen,
    setIsPdfViewerOpen,

    // Live Class Restrictions
    lockedCurriculum
  };

  return (
    <SubjectChapterContext.Provider value={value}>
      {children}
    </SubjectChapterContext.Provider>
  );
}

export function useSubjectChapter() {
  const context = useContext(SubjectChapterContext);
  if (!context) {
    throw new Error('useSubjectChapter must be used within SubjectChapterProvider');
  }
  return context;
}

export function clearSubjectChapterSelection() {
  localStorage.removeItem(STORAGE_KEY);
}
